import { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { canEditResources, canManageOrganizations, getRoleBadgeColor } from "@/mocks/auth";
import { api } from "@/api/client";
import type { ResourceRead, ResourceListResponse, OrganizationRead } from "@/api/types";
import { useAuth } from "@/contexts/AuthContext";

// Display labels → backend ResourceType enum values
const RESOURCE_TYPE_OPTIONS: { label: string; value: string }[] = [
  { label: "RDT Kit",          value: "rdt_kits" },
  { label: "ACT Drug",         value: "act_drugs" },
  { label: "LLIN Net",         value: "mosquito_nets" },
  { label: "Trained Personnel",value: "trained_personnel" },
];
const RESOURCE_TYPE_LABELS: Record<string, string> = Object.fromEntries(
  RESOURCE_TYPE_OPTIONS.map(({ value, label }) => [value, label])
);
const resourceTypeFilterOptions = ["All", ...RESOURCE_TYPE_OPTIONS.map((o) => o.value)];

interface DistrictOption { id: number; name: string; region: string; }

interface ResourceForm {
  resource_type: string;
  quantity: number;
  low_stock_threshold: number;
  notes: string;
  district_id: number;
  org_id: number;
}

export default function Resources() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const displayUser = user!;

  // Data state
  const [items, setItems] = useState<ResourceRead[]>([]);
  const [orgs, setOrgs] = useState<OrganizationRead[]>([]);
  const [apiDistricts, setApiDistricts] = useState<DistrictOption[]>([]);
  const [loading, setLoading] = useState(true);

  // Filter state
  const [filterType, setFilterType] = useState<string>("All");
  const [filterDistrictId, setFilterDistrictId] = useState<number | "All">("All");
  const [filterOrgId, setFilterOrgId] = useState<number | "All">("All");
  const [filterLowStock, setFilterLowStock] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [sortField, setSortField] = useState<keyof ResourceRead>("quantity");
  const [sortAsc, setSortAsc] = useState(false);

  // Modal / CRUD state
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<number | null>(null);
  const [toast, setToast] = useState<{ message: string; type: "success" | "error" } | null>(null);

  const defaultOrgId = orgs.length > 0 ? orgs[0].id : 1;
  const defaultDistrictId = apiDistricts.length > 0 ? apiDistricts[0].id : 1;

  const [form, setForm] = useState<ResourceForm>({
    resource_type: "rdt_kits",
    quantity: 100,
    low_stock_threshold: 20,
    notes: "",
    district_id: defaultDistrictId,
    org_id: defaultOrgId,
  });

  const canEdit = canEditResources(displayUser.role);
  const isAdmin = canManageOrganizations(displayUser.role);

  // Fetch orgs + districts once on mount
  useEffect(() => {
    let cancelled = false;
    async function loadStatic() {
      try {
        const [orgData, distData] = await Promise.all([
          api.organizations() as Promise<OrganizationRead[]>,
          api.districts() as Promise<DistrictOption[]>,
        ]);
        if (!cancelled) {
          setOrgs(orgData ?? []);
          setApiDistricts(distData ?? []);
        }
      } catch {
        if (!cancelled) { setOrgs([]); setApiDistricts([]); }
      }
    }
    loadStatic();
    return () => { cancelled = true; };
  }, []);

  // Re-fetch resources whenever org filter changes (backend filters by org_id for admin/officer)
  useEffect(() => {
    let cancelled = false;
    async function loadResources() {
      setLoading(true);
      try {
        const params: Record<string, string | number | boolean | undefined> = { page: 1, per_page: 500 };
        if (filterOrgId !== "All") params.org_id = filterOrgId as number;
        const resData = await api.resources(params) as ResourceListResponse;
        if (!cancelled) setItems(resData?.items ?? []);
      } catch {
        if (!cancelled) setItems([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    loadResources();
    return () => { cancelled = true; };
  }, [filterOrgId]);

  // Org name lookup by id
  const orgNameById = useMemo(() => {
    const map: Record<number, string> = {};
    for (const o of orgs) map[o.id] = o.name;
    return map;
  }, [orgs]);

  // District name lookup by numeric id (from API)
  const districtNameById = useMemo(() => {
    const map: Record<number, string> = {};
    for (const d of apiDistricts) map[d.id] = d.name;
    return map;
  }, [apiDistricts]);

  const filtered = useMemo(() => {
    let data = [...items];
    if (filterType !== "All") data = data.filter((r) => r.resource_type === filterType);
    if (filterDistrictId !== "All") data = data.filter((r) => r.district_id === filterDistrictId);
    // org filter is handled server-side; skip client-side re-filter
    if (filterLowStock) data = data.filter((r) => r.is_low_stock);
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      data = data.filter(
        (r) =>
          (r.notes ?? "").toLowerCase().includes(q) ||
          (r.district_name ?? "").toLowerCase().includes(q) ||
          r.resource_type.toLowerCase().includes(q)
      );
    }
    return data;
  }, [items, filterType, filterDistrictId, filterOrgId, filterLowStock, searchQuery]);

  const sorted = useMemo(() => {
    return [...filtered].sort((a, b) => {
      const aVal = a[sortField];
      const bVal = b[sortField];
      if (typeof aVal === "number" && typeof bVal === "number") {
        return sortAsc ? aVal - bVal : bVal - aVal;
      }
      return sortAsc
        ? String(aVal ?? "").localeCompare(String(bVal ?? ""))
        : String(bVal ?? "").localeCompare(String(aVal ?? ""));
    });
  }, [filtered, sortField, sortAsc]);

  const lowStockCount = items.filter((r) => r.is_low_stock).length;

  function showToast(message: string, type: "success" | "error" = "success") {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  }

  function openAdd() {
    setEditingId(null);
    setForm({
      resource_type: "rdt_kits",
      quantity: 100,
      low_stock_threshold: 20,
      notes: "",
      district_id: apiDistricts.length > 0 ? apiDistricts[0].id : 1,
      org_id: defaultOrgId,
    });
    setModalOpen(true);
  }

  function openEdit(r: ResourceRead) {
    setEditingId(r.id);
    setForm({
      resource_type: r.resource_type,
      quantity: r.quantity,
      low_stock_threshold: r.low_stock_threshold,
      notes: r.notes ?? "",
      district_id: r.district_id,
      org_id: r.org_id,
    });
    setModalOpen(true);
  }

  async function saveForm() {
    // org_id is derived server-side from the authenticated user — never send it
    const createPayload = {
      resource_type: form.resource_type,
      quantity: form.quantity,
      low_stock_threshold: form.low_stock_threshold,
      notes: form.notes || null,
      district_id: form.district_id,
    };
    const updatePayload = {
      quantity: form.quantity,
      low_stock_threshold: form.low_stock_threshold,
      notes: form.notes || null,
      district_id: form.district_id,
    };
    try {
      if (editingId != null) {
        const updated = await api.updateResource(editingId, updatePayload) as ResourceRead;
        setItems((prev) => prev.map((r) => (r.id === editingId ? updated : r)));
      } else {
        const created = await api.createResource(createPayload) as ResourceRead;
        setItems((prev) => [...prev, created]);
      }
      showToast(t("common_success"));
      setModalOpen(false);
    } catch {
      showToast(t("common_error") ?? "Error saving resource", "error");
    }
  }

  function confirmDelete(id: number) {
    setDeleteConfirmId(id);
  }

  async function doDelete() {
    if (deleteConfirmId == null) return;
    try {
      await api.deleteResource(deleteConfirmId);
      setItems((prev) => prev.filter((r) => r.id !== deleteConfirmId));
      showToast(t("common_delete"), "error");
    } catch {
      showToast(t("common_error") ?? "Error deleting resource", "error");
    }
    setDeleteConfirmId(null);
  }

  function exportCSV() {
    const headers = [
      "ID", t("res_type"), t("res_resource"), t("res_quantity"), t("res_district"), t("res_organization"), "Low Stock", t("res_updated"),
    ];
    const rows = sorted.map((r) => [
      r.id,
      r.resource_type,
      r.notes ?? "—",
      r.quantity,
      r.district_name ?? districtNameById[r.district_id] ?? r.district_id,
      orgNameById[r.org_id] ?? r.org_id,
      r.is_low_stock ? "Yes" : "No",
      r.updated_at.split("T")[0],
    ]);
    const csv = [headers.join(","), ...rows.map((row) => row.map((c) => `"${c}"`).join(","))].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = `resources-export-${new Date().toISOString().split("T")[0]}.csv`;
    anchor.click();
    URL.revokeObjectURL(url);
    showToast(t("reports_exported"));
  }

  const activeFilters = [
    filterType !== "All",
    filterDistrictId !== "All",
    filterOrgId !== "All",
    filterLowStock,
    searchQuery.length > 0,
  ].filter(Boolean).length;

  const typeIcons: Record<string, { bg: string; icon: string; color: string }> = {
    rdt_kits:           { bg: "bg-teal-muted/30",               icon: "ri-test-tube-line",  color: "text-teal" },
    act_drugs:          { bg: "bg-emerald-muted/30",            icon: "ri-capsule-line",    color: "text-emerald" },
    mosquito_nets:      { bg: "bg-blue-500/10",                 icon: "ri-shield-line",     color: "text-blue-400" },
    trained_personnel:  { bg: "bg-amber-muted/30",              icon: "ri-user-heart-line", color: "text-amber" },
  };
  const defaultTypeIcon = { bg: "bg-slate-200 dark:bg-slate-800", icon: "ri-hospital-line", color: "text-slate-500 dark:text-slate-400" };



  return (
    <div className="animate-fade-in space-y-5">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-display font-bold text-slate-900 dark:text-white">{t("res_title")}</h1>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">{t("res_subtitle")}</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {canEdit && (
            <button
              onClick={openAdd}
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-teal text-slate-950 text-sm font-semibold hover:bg-teal-hover transition-colors whitespace-nowrap"
            >
              <i className="ri-add-line text-sm" />
              {t("res_add_resource")}
            </button>
          )}
          <button
            onClick={exportCSV}
            className="flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 text-sm hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors whitespace-nowrap"
          >
            <i className="ri-download-2-line text-sm" />
            {t("res_export_csv")}
          </button>
        </div>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
        <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900/80 p-4">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-teal-muted/40">
              <i className="ri-archive-line text-teal text-sm" />
            </div>
            <span className="text-xs text-slate-500 dark:text-slate-400">{t("res_total_items")}</span>
          </div>
          <p className="text-xl font-bold text-slate-900 dark:text-white font-mono">{items.length}</p>
        </div>
        <div className={`rounded-xl border ${lowStockCount > 0 ? "border-red-300 dark:border-red-500/30 bg-red-50 dark:bg-red-500/10" : "border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900/80"} p-4`}>
          <div className="flex items-center gap-2 mb-2">
            <div className={`w-8 h-8 flex items-center justify-center rounded-lg ${lowStockCount > 0 ? "bg-red-500/20" : "bg-slate-200 dark:bg-slate-800"}`}>
              <i className={`ri-error-warning-line ${lowStockCount > 0 ? "text-red-500 dark:text-red-400" : "text-slate-400 dark:text-slate-500"} text-sm`} />
            </div>
            <span className="text-xs text-slate-500 dark:text-slate-400">{t("res_low_stock")}</span>
          </div>
          <p className={`text-xl font-bold font-mono ${lowStockCount > 0 ? "text-red-500 dark:text-red-400" : "text-slate-900 dark:text-white"}`}>{lowStockCount}</p>
        </div>
        <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900/80 p-4">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-amber-muted/40">
              <i className="ri-map-pin-line text-amber text-sm" />
            </div>
            <span className="text-xs text-slate-500 dark:text-slate-400">{t("res_district")}</span>
          </div>
          <p className="text-xl font-bold text-slate-900 dark:text-white font-mono">
            {new Set(items.map((i) => i.district_id)).size}
          </p>
        </div>
        <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900/80 p-4">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-emerald-muted/40">
              <i className="ri-organization-chart text-emerald text-sm" />
            </div>
            <span className="text-xs text-slate-500 dark:text-slate-400">{t("res_orgs")}</span>
          </div>
          <p className="text-xl font-bold text-slate-900 dark:text-white font-mono">{orgs.length}</p>
        </div>
      </div>

      {/* Filters bar */}
      <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900/80 p-4">
        <div className="flex flex-col lg:flex-row gap-3 flex-wrap">
          <div className="flex items-center gap-2 flex-1 min-w-[200px]">
            <i className="ri-search-line text-sm text-slate-400 dark:text-slate-500" />
            <input
              type="text"
              placeholder={t("res_search")}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="flex-1 bg-transparent text-sm text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-slate-500 outline-none min-w-0"
            />
          </div>
          <div className="h-6 w-px bg-slate-200 dark:bg-slate-800 hidden lg:block" />
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-700 dark:text-slate-300 outline-none focus:border-teal"
          >
            {resourceTypeFilterOptions.map((rt) => (
              <option key={rt} value={rt}>{rt === "All" ? t("res_all_types") ?? "All Types" : RESOURCE_TYPE_LABELS[rt] ?? rt}</option>
            ))}
          </select>

          <select
            value={filterDistrictId}
            onChange={(e) => setFilterDistrictId(e.target.value === "All" ? "All" : Number(e.target.value))}
            className="bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-700 dark:text-slate-300 outline-none focus:border-teal"
          >

            <option value="All">{t("res_all_districts")}</option>
            {apiDistricts.map((d) => (
              <option key={d.id} value={d.id}>{d.name}</option>
            ))}
          </select>

          {isAdmin && (
            <select
              value={filterOrgId}
              onChange={(e) => setFilterOrgId(e.target.value === "All" ? "All" : Number(e.target.value))}
              className="bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-700 dark:text-slate-300 outline-none focus:border-teal"
            >
              <option value="All">{t("res_all_orgs")}</option>
              {orgs.map((o) => (
                <option key={o.id} value={o.id}>{o.name}</option>
              ))}
            </select>
          )}
          <button
            onClick={() => setFilterLowStock(!filterLowStock)}
            className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm transition-colors whitespace-nowrap ${
              filterLowStock
                ? "border-red-400 dark:border-red-500/40 bg-red-50 dark:bg-red-500/10 text-red-500 dark:text-red-400"
                : "border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300"
            }`}
          >
            <i className="ri-error-warning-line text-sm" />
            {t("res_low_stock_only")}
          </button>
          {activeFilters > 0 && (
            <button
              onClick={() => { setFilterType("All"); setFilterDistrictId("All"); setFilterOrgId("All"); setFilterLowStock(false); setSearchQuery(""); }}
              className="px-3 py-2 rounded-lg text-xs text-slate-500 dark:text-slate-500 hover:text-slate-700 dark:hover:text-slate-300 transition-colors"
            >
              {t("common_reset")}
            </button>
          )}
        </div>
      </div>

      {/* Table */}
      {loading ? (
        <div className="min-h-[200px] flex items-center justify-center">
          <div className="w-8 h-8 border-2 border-slate-700 border-t-teal rounded-full animate-spin" />
        </div>
      ) : (
        <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900/80 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-slate-200 dark:border-slate-800">
                  {[
                    { key: "resource_type" as const, label: t("res_type") },
                    { key: "notes" as const, label: t("res_resource") },
                    { key: "quantity" as const, label: t("res_quantity") },
                    { key: "district_name" as const, label: t("res_district") },
                    { key: "org_id" as const, label: t("res_organization") },
                    { key: "is_low_stock" as const, label: t("res_low_stock") },
                    { key: "updated_at" as const, label: t("res_updated") },
                  ].map((col) => (
                    <th
                      key={col.key}
                      onClick={() => {
                        if (sortField === col.key) setSortAsc(!sortAsc);
                        else { setSortField(col.key); setSortAsc(true); }
                      }}
                      className="px-4 py-3 text-xs font-semibold text-slate-500 dark:text-slate-500 uppercase tracking-wider cursor-pointer hover:text-slate-700 dark:hover:text-slate-300 transition-colors select-none"
                    >
                      <span className="flex items-center gap-1">
                        {col.label}
                        {sortField === col.key && (
                          <i className={sortAsc ? "ri-arrow-up-s-line text-teal" : "ri-arrow-down-s-line text-teal"} />
                        )}
                      </span>
                    </th>
                  ))}
                  <th className="px-4 py-3 text-xs font-semibold text-slate-500 dark:text-slate-500 uppercase tracking-wider">{t("res_actions")}</th>
                </tr>
              </thead>
              <tbody>
                {sorted.map((r) => {
                  const isLow = r.is_low_stock;
                  const ti = typeIcons[r.resource_type] ?? defaultTypeIcon;
                  return (
                    <tr key={r.id} className={`border-b border-slate-200/50 dark:border-slate-800/60 hover:bg-slate-50 dark:hover:bg-slate-800/40 transition-colors ${isLow ? "bg-red-50 dark:bg-red-500/5" : ""}`}>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <div className={`w-8 h-8 flex items-center justify-center rounded-lg ${ti.bg}`}>
                            <i className={`${ti.icon} ${ti.color} text-sm`} />
                          </div>
                          <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 border border-slate-200 dark:border-slate-700">
                            {r.resource_type}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div>
                          <p className="text-sm font-medium text-slate-900 dark:text-white">{r.notes ?? "—"}</p>
                          <p className="text-xs text-slate-500 dark:text-slate-500">#{r.id}</p>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div>
                          <p className={`text-sm font-mono font-medium ${isLow ? "text-red-500 dark:text-red-400" : "text-slate-900 dark:text-white"}`}>
                            {r.quantity.toLocaleString()}
                          </p>
                          {isLow && (
                            <p className="text-[10px] text-red-500 dark:text-red-400 mt-0.5 flex items-center gap-1">
                              <i className="ri-error-warning-line" />
                              {t("res_below_threshold")} ({r.low_stock_threshold})
                            </p>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-700 dark:text-slate-300">
                        {r.district_name ?? districtNameById[r.district_id] ?? `District ${r.district_id}`}
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-700 dark:text-slate-300">
                        {orgNameById[r.org_id] ?? `Org ${r.org_id}`}
                      </td>
                      <td className="px-4 py-3">
                        {isLow ? (
                          <span className="inline-flex items-center gap-1 text-xs text-red-500 dark:text-red-400">
                            <span className="w-1.5 h-1.5 rounded-full bg-red-500 dark:bg-red-400" />
                            {t("res_low_stock")}
                          </span>
                        ) : (
                          <span className="text-xs text-emerald font-medium">{t("res_sufficient")}</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-xs text-slate-500 dark:text-slate-500">{r.updated_at.split("T")[0]}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1">
                          {canEdit && (
                            <button
                              onClick={() => openEdit(r)}
                              className="w-8 h-8 flex items-center justify-center rounded-lg text-slate-400 dark:text-slate-500 hover:text-teal hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                              title={t("common_edit")}
                            >
                              <i className="ri-edit-line text-sm" />
                            </button>
                          )}
                          {isAdmin && (
                            <button
                              onClick={() => confirmDelete(r.id)}
                              className="w-8 h-8 flex items-center justify-center rounded-lg text-slate-400 dark:text-slate-500 hover:text-red-500 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-500/10 transition-colors"
                              title={t("common_delete")}
                            >
                              <i className="ri-delete-bin-line text-sm" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {sorted.length === 0 && (
                  <tr>
                    <td colSpan={8} className="px-4 py-12 text-center text-slate-500 dark:text-slate-500 text-sm">
                      {t("reports_no_data")}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <div className="px-4 py-3 border-t border-slate-200 dark:border-slate-800 flex items-center justify-between text-xs text-slate-500 dark:text-slate-500">
            <span>{t("res_showing", { count: sorted.length, total: items.length })}</span>
          </div>
        </div>
      )}

      {/* Add/Edit Modal */}
      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
          <div className="w-full max-w-lg rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 shadow-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200 dark:border-slate-800">
              <h3 className="text-sm font-semibold text-slate-900 dark:text-white">
                {editingId != null ? t("res_edit_resource") : t("res_add_resource")}
              </h3>
              <button
                onClick={() => setModalOpen(false)}
                className="w-8 h-8 flex items-center justify-center rounded-lg text-slate-400 dark:text-slate-500 hover:text-slate-800 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
              >
                <i className="ri-close-line" />
              </button>
            </div>
            <div className="p-5 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs text-slate-500 dark:text-slate-400 mb-1.5">{t("res_type")}</label>
                  <select
                    value={form.resource_type}
                    onChange={(e) => setForm({ ...form, resource_type: e.target.value })}
                    className="w-full bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-900 dark:text-white outline-none focus:border-teal"
                  >
                    {RESOURCE_TYPE_OPTIONS.map((rt) => (
                      <option key={rt.value} value={rt.value}>{rt.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-slate-500 dark:text-slate-400 mb-1.5">{t("res_resource")}</label>
                  <input
                    type="text"
                    value={form.notes}
                    onChange={(e) => setForm({ ...form, notes: e.target.value })}
                    className="w-full bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-900 dark:text-white outline-none focus:border-teal"
                    placeholder="e.g. Standard RDT Kit"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs text-slate-500 dark:text-slate-400 mb-1.5">{t("res_quantity")}</label>
                  <input
                    type="number"
                    value={form.quantity}
                    onChange={(e) => setForm({ ...form, quantity: Number(e.target.value) })}
                    className="w-full bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-900 dark:text-white outline-none focus:border-teal"
                  />
                </div>
                <div>
                  <label className="block text-xs text-slate-500 dark:text-slate-400 mb-1.5">{t("res_min_threshold_label")}</label>
                  <input
                    type="number"
                    value={form.low_stock_threshold}
                    onChange={(e) => setForm({ ...form, low_stock_threshold: Number(e.target.value) })}
                    className="w-full bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-900 dark:text-white outline-none focus:border-teal"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs text-slate-500 dark:text-slate-400 mb-1.5">{t("res_district")}</label>
                  <select
                    value={form.district_id}
                    onChange={(e) => setForm({ ...form, district_id: Number(e.target.value) })}
                    className="w-full bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-900 dark:text-white outline-none focus:border-teal"
                  >
                    {apiDistricts.length === 0 && (
                      <option value="">No districts — add one in Settings</option>
                    )}
                    {apiDistricts.map((d) => (
                      <option key={d.id} value={d.id}>{d.name} ({d.region})</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-slate-500 dark:text-slate-400 mb-1.5">{t("res_organization")}</label>
                  {isAdmin ? (
                    <select
                      value={form.org_id}
                      onChange={(e) => setForm({ ...form, org_id: Number(e.target.value) })}
                      className="w-full bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-900 dark:text-white outline-none focus:border-teal"
                    >
                      {orgs.map((o) => (
                        <option key={o.id} value={o.id}>{o.name}</option>
                      ))}
                    </select>
                  ) : (
                    <div className="w-full bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-500 dark:text-slate-400">
                      {displayUser.organizationName ?? "Your Organization"}
                      <span className="text-[10px] ml-2 text-slate-400 dark:text-slate-600">(set by your account)</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-slate-200 dark:border-slate-800">
              <button
                onClick={() => setModalOpen(false)}
                className="px-4 py-2 rounded-lg text-sm text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-white transition-colors"
              >
                {t("common_cancel")}
              </button>
              <button
                onClick={saveForm}
                className="px-4 py-2 rounded-lg bg-teal text-slate-950 text-sm font-semibold hover:bg-teal-hover transition-colors"
              >
                {editingId != null ? t("common_save") : t("res_add_resource")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete confirmation */}
      {deleteConfirmId != null && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
          <div className="w-full max-w-sm rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 shadow-2xl p-5">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-red-50 dark:bg-red-500/20">
                <i className="ri-error-warning-line text-red-500 dark:text-red-400 text-lg" />
              </div>
              <h3 className="text-sm font-semibold text-slate-900 dark:text-white">{t("res_delete_confirm_title")}</h3>
            </div>
            <p className="text-xs text-slate-500 dark:text-slate-400 mb-5">{t("res_delete_confirm_desc")}</p>
            <div className="flex items-center justify-end gap-2">
              <button
                onClick={() => setDeleteConfirmId(null)}
                className="px-4 py-2 rounded-lg text-sm text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-white transition-colors"
              >
                {t("common_cancel")}
              </button>
              <button
                onClick={doDelete}
                className="px-4 py-2 rounded-lg bg-red-500 text-white text-sm font-semibold hover:bg-red-600 transition-colors"
              >
                {t("common_delete")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div className={`fixed bottom-5 right-5 z-50 px-4 py-3 rounded-xl shadow-lg border text-sm font-medium animate-slide-up ${
          toast.type === "success"
            ? "bg-white dark:bg-slate-900 border-teal/30 text-teal"
            : "bg-white dark:bg-slate-900 border-red-500/30 text-red-500 dark:text-red-400"
        }`}>
          <div className="flex items-center gap-2">
            <i className={toast.type === "success" ? "ri-check-line" : "ri-error-warning-line"} />
            {toast.message}
          </div>
        </div>
      )}
    </div>
  );
}
